/** Cotizador por composicion + lectura del documento de la tarea (design.md §22.5).
 *
 * Sin cotizaciones en el PWA: no hay lista ni acciones de cotizacion. El
 * detalle de tarea conserva la lectura del documento y la exportacion a PDF.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantIdOrNull, tenantScoped } from '@/config/queryClient';
import {
  commercialService,
  type CatalogItemDto,
  type CompositionNode,
  type ListResponse,
  type MobileAttachmentDto,
  type MobileBudgetDto,
  type MobileBudgetItemDto,
  type MobileBudgetSnapshotDto,
  type MobileQuotationDto,
  type MobileRequirementDto,
} from '@/services/commercialService';
import { tasksService, type WorkflowContext } from '@/services/tasksService';
import { useMobileAuth } from '@/state/MobileAuthContext';

export type MobileBudget = MobileBudgetDto;

/** Grupos del catalogo del cotizador: tecnicas (sale) o de venta (additional). */
export type CatalogGroup = 'sale' | 'additional';

export type MobileDocument = MobileRequirementDto | MobileQuotationDto | MobileBudgetDto;

/** Lectura completa del documento de la tarea (requerimiento, cotizacion o presupuesto). */
export function useMobileDocumentQuery(entityCode: string | undefined, entityId: string | undefined) {
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<MobileDocument>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-document', entityCode, entityId)
        : ['tenant', 'no-context', 'mobile-document', entityCode, entityId],
    queryFn: async () => {
      if (entityCode === 'customer_requirement') {
        return (await commercialService.getRequirement(entityId!)) as MobileDocument;
      }
      if (entityCode === 'quotation') {
        return (await commercialService.getQuotation(entityId!)) as MobileDocument;
      }
      return (await commercialService.getBudget(entityId!)) as MobileDocument;
    },
    enabled: tenantId !== null && entityCode !== undefined && entityId !== undefined,
    staleTime: 30_000,
  });
}

export function useQuotationPdfMutation() {
  return useMutation({
    mutationFn: async (id: string) => {
      const blob = await commercialService.exportQuotationPdf(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cotizacion-${id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function useSimulateQuoteMutation() {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => commercialService.simulate(body),
  });
}

/** Compone un requerimiento nuevo e invalida la lista de requerimientos. */
export function useComposeRequirementMutation() {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => commercialService.composeRequirement(body),
    onSuccess: () => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-requirements'),
        });
      }
    },
  });
}

export function useMobileBudgetsQuery() {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<ListResponse<MobileBudget>>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-budgets')
        : ['tenant', 'no-context', 'mobile-budgets'],
    queryFn: () => commercialService.budgets({ page_size: 25 }),
    enabled: tenantId !== null && hasPermission('budgets.view'),
    staleTime: 30_000,
  });
}

export function useMobileBudgetQuery(budgetId: string | null) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<MobileBudget>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-budget', budgetId)
        : ['tenant', 'no-context', 'mobile-budget', budgetId],
    queryFn: () => commercialService.getBudget(budgetId!),
    enabled:
      tenantId !== null && budgetId !== null && budgetId !== '' && hasPermission('budgets.view'),
    staleTime: 30_000,
  });
}

/** Contexto de flujo del presupuesto: solo se muestra si el servidor lo entrega. */
export function useMobileBudgetContextQuery(budgetId: string | null) {
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<WorkflowContext>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-budget-context', budgetId)
        : ['tenant', 'no-context', 'mobile-budget-context', budgetId],
    queryFn: () => tasksService.context('budget', budgetId!),
    enabled: tenantId !== null && budgetId !== null && budgetId !== '',
    staleTime: 30_000,
  });
}

/** Partidas del presupuesto (costos enmascarados por el servidor si es vendedor ciego). */
export function useMobileBudgetItemsQuery(budgetId: string | null) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<MobileBudgetItemDto[]>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-budget-items', budgetId)
        : ['tenant', 'no-context', 'mobile-budget-items', budgetId],
    queryFn: () => commercialService.listBudgetItems(budgetId!),
    enabled:
      tenantId !== null &&
      budgetId !== null &&
      budgetId !== '' &&
      (hasPermission('budget_items.view') || hasPermission('budgets.commercial_items.view')),
    staleTime: 30_000,
  });
}

/** Fotos (snapshots) del presupuesto. */
export function useMobileBudgetSnapshotsQuery(budgetId: string | null) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<MobileBudgetSnapshotDto[]>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-budget-snapshots', budgetId)
        : ['tenant', 'no-context', 'mobile-budget-snapshots', budgetId],
    queryFn: () => commercialService.listBudgetSnapshots(budgetId!),
    enabled:
      tenantId !== null &&
      budgetId !== null &&
      budgetId !== '' &&
      hasPermission('budgets.view_snapshots'),
    staleTime: 30_000,
  });
}

/** Adjuntos del presupuesto (GET /api/v1/budgets/{id}/attachments, espejo de requerimientos). */
export function useMobileBudgetAttachmentsQuery(budgetId: string | null) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<MobileAttachmentDto[]>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-budget-attachments', budgetId)
        : ['tenant', 'no-context', 'mobile-budget-attachments', budgetId],
    queryFn: () => commercialService.budgetAttachments(budgetId!),
    enabled:
      tenantId !== null && budgetId !== null && budgetId !== '' && hasPermission('budgets.view'),
    staleTime: 30_000,
  });
}

/** Subida batch de adjuntos del presupuesto (FormData, 10 MB, tipos del servidor). */
export function useUploadBudgetAttachmentsMutation() {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: { budgetId: string; files: File[] }) =>
      commercialService.uploadAttachments('budgets', input.budgetId, input.files),
    onSuccess: (_data, input) => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-budget-attachments', input.budgetId),
        });
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-budget', input.budgetId),
        });
      }
    },
  });
}

function invalidateBudget(queryClient: ReturnType<typeof useQueryClient>, tenantId: string, budgetId: string) {
  void queryClient.invalidateQueries({
    queryKey: tenantScoped(tenantId, 'mobile-budget', budgetId),
  });
  void queryClient.invalidateQueries({
    queryKey: tenantScoped(tenantId, 'mobile-budget-items', budgetId),
  });
  void queryClient.invalidateQueries({
    queryKey: tenantScoped(tenantId, 'mobile-budget-versions', budgetId),
  });
  void queryClient.invalidateQueries({
    queryKey: tenantScoped(tenantId, 'mobile-budget-attachments', budgetId),
  });
  void queryClient.invalidateQueries({
    queryKey: tenantScoped(tenantId, 'mobile-budget-context', budgetId),
  });
  void queryClient.invalidateQueries({
    queryKey: tenantScoped(tenantId, 'mobile-budgets'),
  });
}

/** Ejecuta una acción de flujo del presupuesto con revisión del documento. */
export function useBudgetActionMutation(budgetId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: { action: string; body?: Record<string, unknown>; revision?: number }) =>
      commercialService.budgetAction(budgetId, input.action, input.body ?? {}, input.revision),
    onSuccess: () => {
      if (tenantId !== null) invalidateBudget(queryClient, tenantId, budgetId);
    },
  });
}

/** Genera la cotización desde un presupuesto aprobado (sin navegar fuera). */
export function useGenerateQuotationMutation(budgetId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: () => commercialService.generateQuotation(budgetId),
    onSuccess: () => {
      if (tenantId !== null) invalidateBudget(queryClient, tenantId, budgetId);
    },
  });
}

export type BudgetItemMutationInput = {
  body: Record<string, unknown>;
  revision?: number;
};

/** Alta de partida comercial (catálogo + stepper, payload igual al de la web). */
export function useAddBudgetItemMutation(budgetId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: BudgetItemMutationInput) =>
      commercialService.addBudgetItem(budgetId, input.body, input.revision),
    onSuccess: () => {
      if (tenantId !== null) invalidateBudget(queryClient, tenantId, budgetId);
    },
  });
}

/** Edición de partida comercial (cantidad/precio/margen, patch mínimo como la web). */
export function useUpdateBudgetItemMutation(budgetId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: { itemId: string; body: Record<string, unknown>; revision?: number }) =>
      commercialService.updateBudgetItem(input.itemId, input.body, input.revision),
    onSuccess: () => {
      if (tenantId !== null) invalidateBudget(queryClient, tenantId, budgetId);
    },
  });
}

/** Eliminación de partida comercial (confirmación en la fila, nunca en modal). */
export function useDeleteBudgetItemMutation(budgetId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: { itemId: string; revision?: number }) =>
      commercialService.deleteBudgetItem(input.itemId, input.revision),
    onSuccess: () => {
      if (tenantId !== null) invalidateBudget(queryClient, tenantId, budgetId);
    },
  });
}

/** Ajustes de descuento/recargo con justificación + quitar ajuste. */
export function useBudgetAdjustmentMutations(budgetId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  function invalidate() {
    if (tenantId !== null) invalidateBudget(queryClient, tenantId, budgetId);
  }
  return {
    applyAdjustment: useMutation({
      mutationFn: (input: BudgetItemMutationInput) =>
        commercialService.applyBudgetAdjustment(budgetId, input.body, input.revision),
      onSuccess: invalidate,
    }),
    clearAdjustment: useMutation({
      mutationFn: (input: { itemId: string; revision?: number }) =>
        commercialService.clearBudgetAdjustment(input.itemId, input.revision),
      onSuccess: invalidate,
    }),
  };
}

/** Simular costeo sin persistir + recalcular y persistir totales. */
export function useBudgetTotalsMutations(budgetId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return {
    simulate: useMutation({
      mutationFn: (body: Record<string, unknown>) => commercialService.simulateBudget(budgetId, body),
    }),
    calculate: useMutation({
      mutationFn: () => commercialService.calculateBudget(budgetId),
      onSuccess: () => {
        if (tenantId !== null) invalidateBudget(queryClient, tenantId, budgetId);
      },
    }),
  };
}

/** Catalogo del cotizador por grupo (sale por defecto); conserva cantidades elegidas. */
export function useMobileCatalogQuery(search?: string, group: CatalogGroup = 'sale') {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  const trimmed = (search ?? '').trim();
  const activeSearch = trimmed.length >= 2 ? trimmed : undefined;
  return useQuery<CatalogItemDto[]>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-catalog', group, activeSearch ?? '')
        : ['tenant', 'no-context', 'mobile-catalog', group, activeSearch ?? ''],
    queryFn: () =>
      commercialService.catalogItems(
        activeSearch ? { page_size: 50, search: activeSearch, group } : { page_size: 50, group },
      ),
    enabled: tenantId !== null && hasPermission('quotations.create'),
    staleTime: 30_000,
  });
}

/** Composicion de un item con la cantidad elegida (o 1); solo hijos, sin importes. */
export function useCatalogCompositionQuery(itemId: string | null, quantity?: number) {
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  const effectiveQuantity = typeof quantity === 'number' && quantity > 0 ? quantity : 1;
  return useQuery<CompositionNode>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-catalog-composition', itemId, effectiveQuantity)
        : ['tenant', 'no-context', 'mobile-catalog-composition', itemId, effectiveQuantity],
    queryFn: () => commercialService.catalogComposition(itemId!, effectiveQuantity),
    enabled: tenantId !== null && itemId !== null && itemId !== '',
    staleTime: 30_000,
  });
}
